import { Button } from '@/components/primitives/Button';
import { HelperActionRows } from '@/components/aiTools/HelperActionRows';
/**
 * BrandFormatter — Brand Formatter tool page (Phase X.3.8).
 *
 * Mirrors AssessmentDrafter: capability gate -> ToolPageShell with the paste
 * form as input and the formatted preview + Save/Download row as output.
 * Download becomes available once the draft is saved (the .docx download
 * reads a persisted draft by id).
 */

import { type ReactNode, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canUseAiTools } from '@/lib/capabilities';
import { DownloadDocxButton } from '@/components/DownloadDocxButton';
import { ToolPageShell, ToolDraftingState } from '@/components/aiTools/ToolPageShell';
import { EmptyAiToolsTile } from '@/components/aiTools/EmptyAiToolsTile';
import { BrandFormatterForm } from '@/components/aiTools/BrandFormatterForm';
import { type BrandFormatMode } from '@/components/aiTools/BrandFormatModeToggle';
import { BrandFormatterOutput } from '@/components/aiTools/BrandFormatterOutput';
import {
  aiToolsApi,
  type BrandFormatDraftResponse,
  type BrandFormatFormInputSnapshot,
} from '@/lib/api';
import { aiSaveKeyOf, type KeyedError } from '@/lib/errorCopy';
import { ErrorNotice } from '@/components/ErrorNotice';
import { AI_TOOLS } from './AiTools';
import { KeptWorkNotice } from '@/components/KeptWorkNotice';
import { keptWorkKey, useKeptWork } from '@/lib/keptWork';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { readAiInputMaxChars } from '@/lib/aiInputLimits';

const TOOL = AI_TOOLS.find((t) => t.slug === 'brand-formatter')!;

/** What an expired sign-in keeps of this page (src/lib/keptWork.ts). */
interface KeptFormat {
  response: BrandFormatDraftResponse;
  formInput: BrandFormatFormInputSnapshot;
  mode: BrandFormatMode;
}

export function BrandFormatter(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  // The formatter's input ceiling, from the tenant config; the factory
  // value until the config has loaded.
  const maxChars = readAiInputMaxChars(useTenantConfig(tenantSlug).data?.config, 'brand_formatter');
  // C2: Peace <-> SPICE brand, lifted here so the Save button + the save call
  // can react to it. Default Peace = today's behavior.
  const [mode, setMode] = useState<BrandFormatMode>('peace');
  const [isGenerating, setIsGenerating] = useState(false);
  const [draftResponse, setDraftResponse] = useState<BrandFormatDraftResponse | null>(null);
  const [lastFormInput, setLastFormInput] = useState<BrandFormatFormInputSnapshot | null>(null);
  // WS-9: failures are stored as registry keys (errorCopy.ts owns the
  // copy); null = no error showing.
  const [saveError, setSaveError] = useState<KeyedError | null>(null);
  const [savedDraftId, setSavedDraftId] = useState<string | null>(null);

  /** The save request, built from the screen at the press; null with no
   *  formatted document yet. */
  function saveRequestOf(): Parameters<typeof aiToolsApi.saveBrandFormatDraft>[1] | null {
    if (!draftResponse || !lastFormInput) return null;
    return {
      ai_invocation_id: draftResponse.ai_invocation_id,
      title: draftResponse.title,
      blocks: draftResponse.blocks,
      form_input: lastFormInput,
      // C2: persist the chosen brand so the .docx downloads in it.
      mode,
    };
  }

  // The request travels as the variables.
  const saveMutation = useMutation({
    mutationFn: (request: Parameters<typeof aiToolsApi.saveBrandFormatDraft>[1] | null) => {
      if (request === null) throw new Error('saveDraft called without a generated draft');
      return aiToolsApi.saveBrandFormatDraft(tenantSlug, request);
    },
    onSuccess: (res) => {
      setSaveError(null);
      setSavedDraftId(res.id);
    },
    onError: (e: unknown) => {
      setSavedDraftId(null);
      setSaveError(aiSaveKeyOf(e));
    },
  });

  // An expired sign-in keeps the formatted document and what it was made
  // from (src/lib/keptWork.ts). Before a format the form keeps its own.
  const kept = useKeptWork<KeptFormat>(
    keptWorkKey(tenantSlug, 'brand-formatter', null),
    () =>
      draftResponse === null || lastFormInput === null || savedDraftId !== null
        ? null
        : { response: draftResponse, formInput: lastFormInput, mode },
    (k) => {
      setDraftResponse(k.response);
      setLastFormInput(k.formInput);
      setMode(k.mode);
      setSavedDraftId(null);
    },
  );
  // Saved: the kept copy has done its job.
  useEffect(() => {
    if (savedDraftId !== null) kept.clear();
  }, [savedDraftId]);

  if (!canUseAiTools(whoami)) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-6">
        <EmptyAiToolsTile />
      </main>
    );
  }

  const handleDraftReady = (
    response: BrandFormatDraftResponse,
    formInput: BrandFormatFormInputSnapshot,
  ): void => {
    setDraftResponse(response);
    setLastFormInput(formInput);
    setSaveError(null);
    setSavedDraftId(null);
  };

  const outputContent: ReactNode | undefined =
    draftResponse !== null ? (
      <div className="space-y-4">
        {kept.restored && <KeptWorkNotice onDiscard={kept.discard} />}
        <BrandFormatterOutput title={draftResponse.title} blocks={draftResponse.blocks} />
        <HelperActionRows
          testId="brand-formatter-save-row"
          primary={
            savedDraftId !== null ? (
              <DownloadDocxButton
                url={aiToolsApi.brandFormatDownloadUrl(tenantSlug, savedDraftId)}
                filename="brand-formatted.docx"
                variant="primary"
                testId="brand-formatter-download-link"
              >
                Download .docx
              </DownloadDocxButton>
            ) : (
              <Button variant="primary" disabled data-testid="brand-formatter-download-disabled">
                Download .docx
              </Button>
            )
          }
          secondary={
            <Button
              type="button"
              onClick={() => saveMutation.mutate(saveRequestOf())}
              disabled={saveMutation.isPending || savedDraftId !== null}
              variant="secondary"
              aria-label="Save draft"
            >
              {savedDraftId !== null
                ? '✓ Saved'
                : saveMutation.isPending
                  ? 'Saving…'
                  : 'Save to drafts'}
            </Button>
          }
        />
        {savedDraftId !== null && (
          <Link
            to={`/t/${tenantSlug}/ai-tools/drafts`}
            className="text-sm text-sage-700 underline hover:text-sage-900"
            data-testid="brand-formatter-saved-link"
          >
            View drafts inbox
          </Link>
        )}
        {savedDraftId === null && (
          <p className="text-xs text-ink-soft">
            Save the draft to enable the Word download and to reopen it later from the drafts inbox.
          </p>
        )}
        {saveError !== null && (
          <ErrorNotice
            surface="brand_draft_save"
            code={saveError.key}
            vars={saveError.vars}
            data-testid="brand-formatter-save-error"
          />
        )}
      </div>
    ) : isGenerating ? (
      <ToolDraftingState />
    ) : undefined;

  return (
    <ToolPageShell
      tool={TOOL}
      tenantSlug={tenantSlug}
      inputTitle="Your notes"
      outputTitle="Formatted document"
      inputPlaceholder={
        <BrandFormatterForm
          tenantSlug={tenantSlug}
          mode={mode}
          onModeChange={setMode}
          onGeneratingChange={setIsGenerating}
          onDraftReady={handleDraftReady}
          hasDraft={draftResponse !== null}
          maxChars={maxChars}
        />
      }
      outputContent={outputContent}
    />
  );
}
